import { ChannelType } from '@/features/channels/types/channel';
import {
  ParsedChannelData,
  ImportError,
  ValidationResult,

} from '@/features/channels/types/import';
import { toast } from 'sonner';

interface ValidationServiceConfig {
  providerId: number;
  versionId: number;
  isSequential: boolean;
  channelTypes: ChannelType[];
  defaultTypeId?: number;
  is_local?: boolean;
  is_duplicate?: boolean;
}


// Updated regex to match the database approach - exclude only control characters
const CHANNEL_NUMBER_REGEX = /^[^\x00-\x1F\x7F]+$/;

export class ValidationService {
  constructor(private config: ValidationServiceConfig) {}

  /**
   * Main validation method - now uses centralized validation service
   */
  async validateData(
    data: ParsedChannelData[]
  ): Promise<ValidationResult> {
    try {
      console.log('Starting validation with data:', data);
      const errors: ImportError[] = [];

      // Run basic format validation first
      console.log('Running format validation');
      const formatErrors = this.validateFormat(data);
      console.log('Format validation results:', formatErrors);

      if (formatErrors.length > 0) {
        errors.push(...formatErrors);
        return {
          isValid: false,
          errors,
          data: undefined
        };
      }

      // Use centralized validation service for duplicate checking
      const { channelValidationService } = await import('../channels/validation');
      
      const validationContext = {
        providerId: this.config.providerId,
        versionId: this.config.versionId,
        isSequential: this.config.isSequential,
        is_local: this.config.is_local,
        is_duplicate: this.config.is_duplicate
      };

      // Convert parsed data to validation format
      const channelsForValidation = data.map(channel => ({
        name: channel.name,
        numbers: channel.numbers.map(num => ({
          typeId: num.typeId,
          typeName: `Type ${num.typeId}`,
          number: num.number || '*'
        }))
      }));

      console.log('Running centralized validation');
      const validation = await channelValidationService.validateBulkChannels(
        channelsForValidation,
        validationContext
      );

      if (!validation.isValid) {
        // Convert validation errors to import errors
        validation.errors.forEach((error, index) => {
          errors.push({
            line: index + 1,
            message: error,
            type: 'validation',
            field: 'channel'
          });
        });
      }

      console.log('All validation errors:', errors);

      return {
        isValid: errors.length === 0,
        errors,
        data: errors.length === 0 ? data : undefined
      };
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "An unknown error occurred during validation");
      throw error;
    }
  }

  /**
   * Validate basic format requirements
   */
  private validateFormat(data: ParsedChannelData[]): ImportError[] {
    const errors: ImportError[] = [];

    data.forEach(item => {
      // Validate name
      if (!item.name?.trim()) {
        errors.push({
          line: item.line,
          message: 'Channel name is required',
          type: 'validation',
          field: 'name'
        });
      }

      // Validate numbers for non-sequential providers only
      // Sequential providers have empty numbers array - validation happens during import
      if (!this.config.isSequential && item.numbers.length > 0) {
        // Check if all numbers are length matches
        if (item.numbers.length !== this.config.channelTypes.length) {
          errors.push({
            line: item.line,
            message: `Expected ${this.config.channelTypes.length} numbers, got ${item.numbers.length}`,
            type: 'validation',
            field: 'numbers'
          });
        }

        // Check if at least one non-asterisk number exists
        const hasValidNumber = item.numbers.some(num => {
          const normalizedNumber = !num.number || num.number.trim() === '' ? '*' : num.number.trim().toUpperCase();
          return normalizedNumber !== '*';
        });

        if (!hasValidNumber) {
          errors.push({
            line: item.line,
            message: 'At least one valid number is required for each channel',
            type: 'validation',
            field: 'number'
          });
          return;
        }

        // Updated number format validation
        item.numbers.forEach(num => {
          const normalizedNumber = !num.number || num.number.trim() === '' ? '*' : num.number.trim().toUpperCase();

          // Validate format for non-asterisk values
          if (normalizedNumber !== '*' && !CHANNEL_NUMBER_REGEX.test(normalizedNumber)) {
            errors.push({
              line: item.line,
              message: `Invalid number format: ${num.number}. Control characters are not allowed`,
              type: 'validation',
              field: 'number'
            });
          }
        });
      }
    });

    return errors;
  }

  /**
   * Simple helper to batch arrays for processing
   */
} 